import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { AddPreSoldMembershipFormValues } from 'src/components/AddPreSoldMembershipModal/add-pre-sold-membership-form';
import {
  convertPriceToCents,
  getIntervalFromLengthInMonths,
} from 'src/components/AddPreSoldMembershipModal/util';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { formatProjectDate } from 'src/components/SidePanelTabs/Projects/use-project-card-form';
import {
  Address,
  mapAddress,
  Project,
  ProjectStage,
  ProjectStages,
  useCreateProject,
  useGroupRules,
  useProjectStages,
  UserGroupRules,
} from 'src/graphql';
import { usePreSoldMembership } from 'src/graphql/entities/pre-sold-membership/pre-sold-membership';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState, useShowModals } from 'src/store/modals';
import { AddressRaw } from 'src/types';
import { useNotifications } from 'src/utils/hooks';
import {
  AddProjectFormField,
  AddProjectFormValues,
  useAddProjectForm,
} from './use-add-project-form';

export const AddProjectModal = () => {
  const { closeModal } = useModalMethods(Modals.AddProject);
  const { isOpened, modalProps } = useModalState(Modals.AddProject);
  const [showAddSiteModal, showAddProjectModal] = useShowModals(
    Modals.AddSiteModal,
    Modals.AddProject,
  );
  const { showModal: openPsmModal, closeModal: closePsmModal } = useModalMethods(
    Modals.AddPreSoldMembership,
  );
  const { groupRules } = useGroupRules();

  const canUpdateSLA = Boolean(groupRules[UserGroupRules.CanUpdateSLAs]);
  const { createProject } = useCreateProject({
    sort: modalProps?.sort,
    filters: modalProps?.filters,
    limit: modalProps?.limit,
    offset: modalProps?.offset,
  });
  const { showSuccessAlert } = useNotifications();

  const { onSuccessSubmit } = modalProps || {};

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { createPreSoldMembership } = usePreSoldMembership();

  const createOnSubmitPsm = useCallback(
    (addressId: string, partnerId: number) => async (data: AddPreSoldMembershipFormValues) => {
      const { intervalCount, intervalUnit } = getIntervalFromLengthInMonths(
        data.lengthInMonths,
      );
      const psmPayload = {
        addressId,
        partnerId,
        amount: convertPriceToCents(data.price),
        intervalUnit,
        intervalCount,
        partnerProductId: data.partnerProductId,
        isScheduled: false,
      };

      closePsmModal();
      const result = await createPreSoldMembership(psmPayload);
      if (!result?.errors) {
        showSuccessAlert('Pre-sold membership created successfully');
      }
    },
    [closePsmModal, createPreSoldMembership, showSuccessAlert],
  );

  const { projectStages: allProjectStages, loading: projectStagesLoading } =
    useProjectStages();

  const projectStages = useMemo(() => {
    return allProjectStages
      .filter((stage) => stage.id !== ProjectStages.ProposalLost)
      .sort((a, b) => a.id - b.id);
  }, [allProjectStages]);

  const getDefaultFormValues = useCallback(
    (projectStages: ProjectStage[]) => {
      const defaultProjectStage = projectStages.find(
        (stage) => stage.id === ProjectStages.ActiveProject,
      );
      return (
        modalProps?.prevFormValues || {
          [AddProjectFormField.Address]: modalProps?.address
            ? {
                id: modalProps.address.addressId || '',
                label: modalProps.address.fullAddress || '',
                partnerId: modalProps.address.partnerId,
                hasPreSoldMembership: !!modalProps.address.preSoldMemberships?.length,
              }
            : null,
          [AddProjectFormField.Name]: '',
          [AddProjectFormField.ProjectStage]: defaultProjectStage,
          [AddProjectFormField.ProjectManager]: null,
          [AddProjectFormField.SigningDate]: null,
          [AddProjectFormField.EstimatedDay1]: null,
          [AddProjectFormField.WarrantyExpirationDate]: null,
          [AddProjectFormField.Day1Date]: null,
          [AddProjectFormField.TosAccepted]: modalProps?.address?.tosAccepted === 'Yes',
          [AddProjectFormField.WillReceiveEmails]: false,
          [AddProjectFormField.HasPreSoldMembership]: false,
        }
      );
    },
    [modalProps?.address, modalProps?.prevFormValues],
  );

  const formMethods = useForm<AddProjectFormValues>({
    defaultValues: getDefaultFormValues(projectStages),
  });

  const formIsLoading = projectStagesLoading;

  useEffect(() => {
    if (projectStages) {
      formMethods.reset(getDefaultFormValues(projectStages));
    }
  }, [getDefaultFormValues, formMethods, projectStages]);

  const { handleSubmit } = formMethods;

  const restoreAddProjectModal = useCallback(
    (site?: Address) => {
      const currentFormValues = formMethods.getValues();
      if (currentFormValues) {
        showAddProjectModal({
          prevFormValues: {
            ...currentFormValues,
            ...(!!site && {
              [AddProjectFormField.Address]: {
                id: site.addressId,
                label: site.fullAddress ?? '',
                partnerId: site.partnerId,
                hasPreSoldMembership: !!site.preSoldMemberships?.length,
              },
            }),
          },
        });
      }
    },
    [formMethods, showAddProjectModal],
  );

  const addNewSite = useCallback(() => {
    showAddSiteModal({
      closeOnSiteCreate: true,
      onSuccessSubmit: (site: unknown) => {
        restoreAddProjectModal(mapAddress(site as AddressRaw));
      },
      onCloseModal: () => {
        restoreAddProjectModal();
      },
    });
  }, [restoreAddProjectModal, showAddSiteModal]);

  const { addProjectFormFields } = useAddProjectForm({
    projectStages,
    formMethods,
    addNewSite,
  });

  const onSubmit = useCallback(
    async (data: AddProjectFormValues) => {
      const addressPartnerId = data?.[AddProjectFormField.Address]?.partnerId;
      const partnerId = selectedPartnerId ?? addressPartnerId;

      if (!data?.[AddProjectFormField.Address]?.id || !partnerId) {
        return;
      }

      closeModal();

      const payload = {
        partnerId,
        addressId: data[AddProjectFormField.Address].id,
        name: data[AddProjectFormField.Name],
        projectStageId: data[AddProjectFormField.ProjectStage].id,
        warrantyExpiration:
          data[AddProjectFormField.ProjectStage].id === ProjectStages.ServiceClient &&
          !!data[AddProjectFormField.WarrantyExpirationDate]
            ? formatProjectDate(data[AddProjectFormField.WarrantyExpirationDate])
            : null,
        projectManagerId: data[AddProjectFormField.ProjectManager]?.id,
        signingDate:
          data[AddProjectFormField.ProjectStage].id === ProjectStages.Proposal &&
          !!data[AddProjectFormField.SigningDate]
            ? formatProjectDate(data[AddProjectFormField.SigningDate])
            : null,
        estimatedDay1:
          data[AddProjectFormField.ProjectStage].id === ProjectStages.ActiveProject &&
          !!data[AddProjectFormField.EstimatedDay1]
            ? formatProjectDate(data[AddProjectFormField.EstimatedDay1])
            : null,
        day1Date:
          data[AddProjectFormField.ProjectStage].id === ProjectStages.ServiceClient &&
          !!data[AddProjectFormField.Day1Date]
            ? formatProjectDate(data[AddProjectFormField.Day1Date])
            : null,
        tosAccepted: data[AddProjectFormField.TosAccepted] ? 'Yes' : 'No',
        willReceiveEmails: data[AddProjectFormField.WillReceiveEmails],
      };

      const result = await createProject(payload, modalProps?.address);
      if (result.errors) {
        return;
      }

      if (data[AddProjectFormField.HasPreSoldMembership] && canUpdateSLA) {
        openPsmModal({
          addressId: data[AddProjectFormField.Address].id,
          onSubmit: createOnSubmitPsm(data[AddProjectFormField.Address].id, partnerId),
        });
      }
      onSuccessSubmit?.(result.data?.createProjectWithSideEffects.project as Project);
      showSuccessAlert('Project added successfully');
    },
    [
      selectedPartnerId,
      closeModal,
      createProject,
      modalProps?.address,
      canUpdateSLA,
      onSuccessSubmit,
      showSuccessAlert,
      openPsmModal,
      createOnSubmitPsm,
    ],
  );

  return (
    <Dialog open={isOpened} onClose={closeModal} fullWidth>
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
        }}
      >
        <Stack direction="row" spacing={2}>
          <Typography variant="overline">Add Project</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <GenericForm<AddProjectFormValues>
          mt={2}
          formMethods={formMethods}
          loading={formIsLoading}
        >
          {addProjectFormFields}
        </GenericForm>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={formIsLoading}
          onClick={handleSubmit(onSubmit)}
          sx={{
            color: (theme) => theme.palette.custom.OV.sys.primary,
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
